/**
 * Auth Service Client
 *
 * HTTP client for interacting with the backend auth-service
 * instead of directly calling AWS Cognito SDK.
 */

import logger from "../utils/logger";

/**
 * Get the auth-service base URL based on environment
 *
 * For local development, use staging auth-service by default.
 * To use local auth-service, set VITE_AUTH_SERVICE_URL=http://localhost:8080
 */
function getAuthServiceBaseUrl(): string {
  if (typeof window === "undefined") {
    return "";
  }

  // Allow override via environment variable
  if (import.meta.env.VITE_AUTH_SERVICE_URL) {
    return import.meta.env.VITE_AUTH_SERVICE_URL;
  }

  const host = window.location.host;

  // Development/local - use staging by default
  if (host.includes("localhost")) {
    return "https://auth.iwoca-dev.co.uk";
  }

  // Staging
  if (host.includes("stage") || host.includes("iwoca-dev")) {
    return "https://auth.iwoca-dev.co.uk";
  }

  // Production
  return "https://auth.iwoca.co.uk";
}

/**
 * Base fetch wrapper with error handling
 */
async function authFetch<T>(
  endpoint: string,
  options: RequestInit = {},
): Promise<T> {
  const url = `${getAuthServiceBaseUrl()}${endpoint}`;

  const defaultOptions: RequestInit = {
    credentials: "include",
    headers: {
      "Content-Type": "application/json",
      ...options.headers,
    },
  };

  const response = await fetch(url, { ...defaultOptions, ...options });

  if (!response.ok) {
    const errorData = await response.json().catch(() => ({
      message: "Request failed",
    }));

    throw {
      status: response.status,
      code: errorData.code || `HTTP_${response.status}`,
      message: errorData.message || response.statusText,
    };
  }

  return response.json();
}

// ============================================================================
// Request/Response Types
// ============================================================================

export interface InitiateMagicLinkRequest {
  email: string;
  redirect_to?: string;
}

export interface MessageResponse {
  message: string;
}

export interface AuthServiceError {
  status: number;
  code: string;
  message: string;
}

// ============================================================================
// API Functions
// ============================================================================

/**
 * Initiate magic link authentication
 * Sends magic link email to user
 */
export async function initiateMagicLink(
  request: InitiateMagicLinkRequest,
): Promise<MessageResponse> {
  logger.info("Auth-service: initiate magic link", { email: request.email });

  try {
    const response = await authFetch<MessageResponse>(
      "/login/magic-link/initiate",
      {
        method: "POST",
        body: JSON.stringify(request),
      },
    );

    logger.info("Auth-service: initiate magic link succeeded");
    return response;
  } catch (error: any) {
    logger.error("Auth-service: initiate magic link failed", {
      error: error.message,
      code: error.code,
    });
    throw error;
  }
}
